import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Clock, Users, Plane, Building2, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios, { API } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
}

export default function ProgramDetail() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/public/programs/${slug}`).then(({ data }) => setProgram(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-20"><EmptyState title="Program tidak ditemukan" description="Program yang Anda cari mungkin sudah tidak tersedia." /></div>;
  if (!program) return <LoadingScreen />;

  return (
    <div data-testid="program-detail-page">
      <section className="relative flex min-h-[50vh] items-center overflow-hidden">
        <img src={program.cover_image} alt={program.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#084C3A]/95 via-[#0F6B4F]/50 to-transparent" />
        <div className="relative mx-auto max-w-5xl px-4 py-20 sm:px-6">
          <Badge className="rounded-full bg-white/90 text-[#0F6B4F] capitalize">{program.type}</Badge>
          <h1 className="mt-4 font-heading text-3xl font-bold text-white sm:text-4xl">{program.name}</h1>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-[#1F2937]">Deskripsi Program</h2>
            <p className="mt-3 text-base leading-relaxed text-[#64748B]">{program.description}</p>

            <h3 className="mt-8 font-heading text-lg font-semibold text-[#1F2937]">Fasilitas</h3>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(program.facilities || []).map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[#1F2937]"><CheckCircle2 className="h-4 w-4 text-[#0F6B4F]" /> {f}</li>
              ))}
            </ul>

            {program.guide_info && (
              <div className="mt-8 flex items-center gap-4 rounded-2xl bg-[#F5F9F7] p-5">
                <img src={program.guide_info.photo_url} alt={program.guide_info.name} className="h-16 w-16 rounded-full object-cover" />
                <div>
                  <p className="font-heading font-semibold text-[#1F2937]">{program.guide_info.name}</p>
                  <p className="text-sm text-[#64748B]">{program.guide_info.position}</p>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-[#0F6B4F]/10 bg-white p-6 shadow-[0_2px_16px_rgba(15,107,79,0.06)]" data-testid="program-detail-sidebar">
            <div className="flex items-center gap-2 rounded-xl bg-[#FFFBEB] px-4 py-3 text-[#92720C]">
              <Sparkles className="h-4 w-4 shrink-0" />
              <p className="text-sm font-medium">Info harga akan disampaikan admin setelah pendaftaran Anda disetujui.</p>
            </div>
            <div className="mt-5 space-y-3 text-sm text-[#1F2937]">
              <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#64748B]" /> Keberangkatan: {formatDate(program.departure_date)}</p>
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#64748B]" /> Durasi: {program.duration_days} Hari</p>
              <p className="flex items-center gap-2"><Users className="h-4 w-4 text-[#64748B]" /> Sisa Kuota: {Math.max(program.quota - program.quota_taken, 0)} / {program.quota}</p>
              {program.airline && <p className="flex items-center gap-2"><Plane className="h-4 w-4 text-[#64748B]" /> {program.airline}</p>}
              {program.hotel && <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#64748B]" /> {program.hotel}</p>}
              <p className="text-xs text-[#64748B]">Batas Pendaftaran: {formatDate(program.registration_deadline)}</p>
            </div>
            <Button data-testid="program-detail-register-button" onClick={() => navigate("/register")} className="mt-6 h-11 w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90">
              Daftar Program
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
